import $ from 'jquery';
import 'custom-event-polyfill';

import { InfiniteScrollRequest } from './request';

import { InViewport } from '../utils/in-viewport';
import { Store } from "../utils/store";
import { log } from "../utils/logger";

const { infiniteScrollOff } = window.infiniteScrollParams;

/**
 * This class controls the infinite scroll throughout the site.
 * This class extends on the request and definitions to make the file smaller and break the code up.
 *
 * ===== EVENT LISTENING =====
 * The event to listen for new infinite scroll sections is infiniteScrollSectionLoaded
 *
 * Vanilla JS example:
 * document.addEventListener( 'DOMContentLoaded', () => {
 * 	   document.addEventListener( 'infiniteScrollSectionLoaded', ( event ) => {
 * 		   console.log( event.detail );
 * 	   } );
 * } );
 *
 * jQuery example:
 * ( function( $ ) {
 * 	   $( document ).ready( function() {
 * 		   $( document ).on( 'infiniteScrollSectionLoaded', ( event ) => {
 * 			   console.log( event.detail );
 * 		   } );
 * 	   } );
 * }( jQuery ) );
 */
class InfiniteScroll extends InfiniteScrollRequest {
	/**
	 * Initialize the app.
	 *
	 * @param {string} mainContentCtnId
	 * @param {string} spinnerCtnId
	 * @param {string} noMoreCtnId
	 */
	constructor( mainContentCtnId = 'infinite_scroll_content', spinnerCtnId = 'infinite_scroll_load_more_spinner', noMoreCtnId = 'infinite_scroll_no_more_posts' ) {

		if ( ! infiniteScrollOff ) {
			super( mainContentCtnId, spinnerCtnId, noMoreCtnId );

			// Check that elements are on this page and setup.
			if ( this.mainContentCtn.length && this.spinnerCtn.length && this.noMoreCtn.length ) {
				this.setup();

				log( {
					type: 'info',
					message: 'All Elements for Infinite Scroll exist.',
				} );
			}
		}
	}

	setup() {
		// Setup the window based global vars.
		this.setupSectionDetails();

		// Hide any pagination.
		this.hidePagination();

		/**
		 * Initial gathering and localizing of elements and data from the initial page load.
		 */
		this.setupLatestContentSection();
		this.storeArticleData();
		this.storeSectionData();

		/**
		 * Update the Tracking as done on the first load to prevent duplicates.
		 */
		if ( 'single' === this.pageType ) {
			const articleID = this.articles.keysFirst();

			if ( articleID ) {
				this.gaTrackingSingleArticle( articleID, true );
			}
		} else {
			const sectionIndex = this.sections.keysFirst();

			if ( sectionIndex ) {
				this.gaTrackingSections( sectionIndex, true  );
			}
		}

		this.bindEvents();
	}

	/**
	 * Setup window global variables for ease of use within this functionality and others,
	 *
	 * @returns {number}
	 */
	setupSectionDetails() {
		window.inViewport = {
			direction: 'down',
			infiniteScrollSections: {
				loaded: 1,
				current: 1,
				sectionsInViewport: [ 1 ],
				count() {
					return window.inViewport.infiniteScrollSections.sectionsInViewport.length;
				},
			},
			setup() {
				let scrollPos = 0;
				window.addEventListener( 'scroll', function() {
					let top = ( document.body.getBoundingClientRect() ).top;
					window.inViewport.direction = ( top > scrollPos ) ? 'up' : 'down';
					scrollPos = top;
				} );
			},
		};

		window.inViewport.setup();
	}

	/** ------------------------------------------------------------------------------------------
	 * Actions and Events.
	 * ------------------------------------------------------------------------------------------ */

	/**
	 * Bind any events.
	 */
	bindEvents() {
		$( window ).scroll( () => {
			if ( ! this.end ) {
				this.preFetch();
				this.showMore();
				this.updateSectionsInViewport();
			}
		} );
	}

	/**
	 * Triggers the infinite scroll section loaded.
	 */
	triggerEvents() {
		// eslint-disable-next-line no-undef
		const infiniteScrollEvent = new CustomEvent( 'infiniteScrollSectionLoaded', {
			detail: {
				sectionIndex: this.currentSectionIndex,
				sectionContainer: this.latestContentSectionCtn,
			},
		} );

		// Dispatch the event for other apps, such as lazy loaded images to bind on new section.
		document.dispatchEvent( infiniteScrollEvent );
	}

	/**
	 * Updates the sections in the viewport.
	 */
	updateSectionsInViewport() {
		this.contentSectionsInViewport = this.contentSectionCtns.filter( '[data-in-viewport="true"]' );

		// Update the information for the sections in the viewport.
		window.inViewport.infiniteScrollSections.sectionsInViewport = [];

		this.contentSectionsInViewport.each( ( i, el ) => {
			const { contentSectionIndex } = el.dataset;
			const sectionIndexCurrent = this.sections.indexOfData( contentSectionIndex );
			const sectionIndex = ( sectionIndexCurrent + 1);
			// Holds the section indexes for the sections in the viewport.
			if ( false !== sectionIndexCurrent ) {
				window.inViewport.infiniteScrollSections.sectionsInViewport.push( sectionIndex )
			}

			// Holds the current, main section in viewport, only applicable when there is one section in the viewport.
			if ( 1 === this.contentSectionsInViewport.length ) {
				if ( false !== sectionIndexCurrent ) {
					window.inViewport.infiniteScrollSections.current = sectionIndex;
				}
			}
		} );

		if ( 1 === this.contentSectionsInViewport.length ) {
			if ( 'single' === this.pageType ) {
				const article = this.contentSectionsInViewport.find( '[data-article-id]' );
				const articleID = article.attr( 'data-article-id' );
				if ( article.length ) {

					if ( articleID !== this.currentArticleID ) {
						this.previousArticleID = this.currentArticleID;
						this.currentArticleID = articleID;
					}

					this.updateBrowserLocation( articleID );
					this.updateSinglePageTrackers( articleID );
				}
			} else {
				// Update tracking code for pages other than single,
				// e.g homepage, category, author and tag.
				this.updateSectionTrackers( window.inViewport.infiniteScrollSections.loaded );
			}
		}
	}

	/**
	 * Update the URL and page title for single articles.
	 *
	 * @param {string} articleID
	 */
	updateBrowserLocation( articleID ) {
		if ( 'single' === this.pageType ) {
			const articleData = this.articles.get( articleID );
			const { articleTitle, articlePermalink } = articleData;

			if ( document.title !== articleTitle ) {
				document.title = articleTitle;
				window.history.pushState( null, articleTitle, articlePermalink );
			}
		}
	}

	/** ------------------------------------------------------------------------------------------
	 * Tracking.
	 * ------------------------------------------------------------------------------------------ */

	/**
	 * Given the article ID update the GA tracking for single article page type.
	 *
	 * @param {string} articleID
	 * @param {boolean} setNotSend To set the item as being tracked without sending.
	 */
	updateSinglePageTrackers( articleID, setNotSend = false ) {
		if ( 'single' === this.pageType ) {
			this.gaTrackingSingleArticle( articleID, setNotSend );
		}
	}

	/**
	 * General update trackers code for index type pages that have content sections not single articles.
	 *
	 * @param sectionIndex
	 * @param {boolean} setNotSend To set the item as being tracked without sending.
	 */
	updateSectionTrackers( sectionIndex, setNotSend = false ) {
		if ( 'single' !== this.pageType ) {
			this.gaTrackingSections( sectionIndex, setNotSend );
		}
	}

	/**
	 * Tracking for Google Analytics on index type pages with sections rather then single articles.
	 *
	 * @param {string} articleID The article ID used to fetch and update storage.
	 * @param {boolean} setNotSend To set the item as being tracked without sending.
	 */
	gaTrackingSingleArticle( articleID, setNotSend = false ) {
		const articleData = this.articles.get( articleID );
		// Get the flag from the section data to see if GA has been tracked for this section.
		const { gaTracked } = articleData;

		if ( ! gaTracked && setNotSend ) {
			// Update as being tracked, prevent duplicate tracking.
			articleData.gaTracked = true;
			this.sections.set( articleID, articleData );

		} else if ( ! gaTracked && 'undefined' !== typeof window.dataLayer ) {
			// Update as being tracked, prevent duplicate tracking.
			articleData.gaTracked = true;
			this.sections.set( articleID, articleData );

			// TODO. GA Tracking code.

			console.log( 'GA', 'TODO infiniteScrollPage' );
			console.log( 'GA', 'TODO add tracking' );
		}
	}

	/**
	 * Tracking for Google Analytics on index type pages with sections rather then single articles.
	 *
	 * @param sectionIndex
	 * @param {boolean} setNotSend To set the item as being tracked without sending.
	 */
	gaTrackingSections( sectionIndex, setNotSend = false  ) {
		const sectionData = this.sections.get( sectionIndex );
		// Get the flag from the section data to see if GA has been tracked for this section.
		const { gaTracked } = sectionData;

		if ( ! gaTracked && setNotSend ) {
			// Update as being tracked, prevent duplicate tracking.
			sectionData.gaTracked = true;
			this.sections.set( sectionIndex, sectionData );

		} else if ( ! gaTracked && 'undefined' !== typeof window.dataLayer ) {
			sectionData.gaTracked = true;
			this.sections.set( sectionIndex, sectionData );

			// TODO. GA Tracking code.
			console.log( 'GA', 'TODO infiniteScrollPage' );
			console.log( 'GA', 'TODO add tracking' );
		}
	}

	/** ------------------------------------------------------------------------------------------
	 * Loading more sections.
	 * ------------------------------------------------------------------------------------------ */

	/**
	 * Fetch more articles when the mid way point of the current content section container is inline with or
	 * greater than the bottom of the viewport. This is only done if there is no current fetch and the
	 * sections storage is empty.
	 */
	preFetch() {

		if ( ( ! this.fetching && 0 === this.loadMoreSectionQueue.length ) &&
			( this.bottomOfViewPort() >= this.fetchMoreTrigger() ) ) {

			// Fetch more however only allow 3 consecutive fails.
			if ( this.paged <= this.maxNumPages && 3 > this.consecutiveFailedRequests ) {
				this.getArticles();
			}
		}
	}

	/**
	 * Show more articles when the bottom of the content container is inline with or
	 * greater than the bottom of the viewport.
	 */
	showMore() {
		if ( this.loadMoreSectionQueue.length &&
			( this.bottomOfViewPort() >= this.showMoreTrigger() ) ) {
			this.loadNext();
		} else if ( this.noMorePages && ! this.end &&
			( this.bottomOfViewPort() >= this.showMoreTrigger() ) ) {
			this.noMore( true );
		}
	}

	/**
	 * This goes over a bunch of steps to display the next section.
	 *
	 * The window.inViewport.infiniteScrollSections.loaded, is used by external aps such as ad tech.
	 * The next section is pulled from the sections storage and appended to the current container.
	 * Sets the current section along with article containers.
	 * Stores the article data for later use.
	 * Finally triggers the event for external apps to listen for.
	 */
	loadNext() {
		window.inViewport.infiniteScrollSections.loaded++; // Increment the section index for other functionality.

		this.currentSectionIndex++; // Increment the section.

		// The following order is important.
		const nextSection = this.loadMoreSectionQueue.shift();
		this.latestContentSectionCtn.after( nextSection );
		this.setupLatestContentSection();
		this.storeArticleData();
		this.storeSectionData();
		this.triggerEvents();

		this.hidePagination();
	}

	/** ------------------------------------------------------------------------------------------
	 * Storage.
	 * ------------------------------------------------------------------------------------------ */

	/**
	 * Sets the data on all of the articles from infinite scroll page loads.
	 *
	 * This data is received from the data attributes
	 */
	storeArticleData() {
		if ( this.latestContentSectionArticleCtns.length ) {
			this.latestContentSectionArticleCtns.each( ( i, el ) => {
				const { articleId, articleTitle, articlePermalink } = el.dataset;
				const articleData = {
					articleId,
					articleTitle,
					articlePermalink,
				};

				// Extra data for single articles.
				if ( 'single' === this.pageType ) {
					articleData.gaTracked = false;
				}

				// Store all of the article data by article id.
				this.articles.set( articleId, articleData );
			} );
		}
	}

	/**
	 * Sets the data on all of the articles from infinite scroll page loads.
	 *
	 * This data is received from the data attributes
	 */
	storeSectionData() {
		if ( this.latestContentSectionCtn.length ) {
			this.latestContentSectionCtn.each( ( i, el ) => {
				const sectionIndex = window.inViewport.infiniteScrollSections.loaded;
				const { contentSectionIndex } = el.dataset;
				// Store useful information about this section.
				const sectionData = {
					sectionIndex,
					contentSectionIndex,
				};

				// Extra data for pages not singe articles.
				if ( this.isIndexPage() ) {
					sectionData.gaTracked = false;
				}

				// Store all of the article data by article id.
				this.sections.set( sectionIndex, sectionData );
			} );
		}
	}

	/** ------------------------------------------------------------------------------------------
	 * General utils.
	 * ------------------------------------------------------------------------------------------ */

	/**
	 *
	 */
	setupLatestContentSection() {
		// The last content section appended. Also used on initial page load.
		this.latestContentSectionCtn = this.mainContentCtn.find( '.content-section:last' );

		if ( this.latestContentSectionCtn.length ) {
			// With the current section set a new instance of InViewport for the element.
			new InViewport( this.latestContentSectionCtn );

			// Update the jquery instance of section containers.
			this.contentSectionCtns = this.mainContentCtn.find( '.content-section' );

			// Store the jQuery elements for latter use.
			this.latestContentSectionArticleCtns = this.latestContentSectionCtn.find( '[data-article-id]' );
		}
	}

	/** ------------------------------------------------------------------------------------------
	 * Measurements.
	 * ------------------------------------------------------------------------------------------ */

	/**
	 * The base of the view port is calculated by the scroll to top plus the window height.
	 *
	 * return {integer}
	 */
	bottomOfViewPort() {
		return window.pageYOffset + window.innerHeight;
	}

	bottomOfMainContentCtn() {
		return this.mainContentCtn.offset().top + this.mainContentCtn.outerHeight( true );
	}

	/**
	 * The point of the content container which is calculated by the top of element to top of page
	 * plus the container portion as a height.
	 *
	 * return {integer}
	 */
	fetchMoreTrigger() {
		return Math.trunc( this.latestContentSectionCtn.offset().top + ( this.latestContentSectionCtn.outerHeight( true ) * .25 ) );
	}

	/**
	 * The point of the content container which is calculated by the top of element to top of page
	 * plus the container portion as a height.
	 *
	 * return {integer}
	 */
	showMoreTrigger() {
		return Math.trunc( this.latestContentSectionCtn.offset().top + ( this.latestContentSectionCtn.outerHeight( true ) * .75 ) );
	}

	isIndexPage() {
		return 'homepage' === this.pageType ||
			'blog' === this.pageType ||
			'category' === this.pageType ||
			'archive' === this.pageType ||
			'author' === this.pageType ||
			'tag' === this.pageType;
	}

}

// eslint-disable-next-line no-shadow
( function( $ ) {
	'use strict';
	$( document ).ready( function() {
		new InfiniteScroll();
	} );
// eslint-disable-next-line no-undef
}( jQuery ) );
